import React from "react";
import ReactDOMServer from "react-dom/server";

import { faChevronDown, faChevronRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Node as ProseMirrorNode, ResolvedPos } from "prosemirror-model";
import { EditorState, NodeSelection, Plugin, PluginKey, TextSelection, Transaction } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView } from "prosemirror-view";
import { commitMutation, graphql } from "relay-runtime";
import invariant from "tiny-invariant";

import type { DOMComponent } from "~/lib/dom";
import { Extension } from "~/lib/prose/extension";
import { HEADING_MAX_LEVEL } from "~/lib/prose/nodes/header";
import { BlockTreeNode, getBlockBoundaryState } from "~/lib/prose/plugins/blockBoundary";
import { getContext } from "~/lib/prose/plugins/context";
import { currentlyDragging } from "~/lib/prose/plugins/multiselect";
import { closestBlock, isBlock } from "~/lib/prose/utils";
import * as sets from "~/lib/sets";
import { environment } from "~/models";

import { BlockSelection } from "../blockselection";
import { getHeaderTextSize, posInSubheading } from "../nodes/header";

import type { collapse_toggleCollapsedMutation as ToggleCollapsedMutation } from "~/__generated__/collapse_toggleCollapsedMutation.graphql";

import styles from "../css/collapse.module.css";

export function collapsePluginEnabled(state: EditorState): boolean {
	return !!CollapsePluginKey.getState(state);
}

export function isCollapsible(state: EditorState, node: ProseMirrorNode): boolean {
	const { blockTree } = getBlockBoundaryState(state) ?? {};

	// NOTE: H2 nodes are still called "header-one"
	if (node.type.name !== "header-one") return false;

	const treeNode = blockTree?.getNodeByBlockKey(node.attrs.blockId);
	if (!treeNode?.children.length) return false;

	for (const block of treeNode.containedBlocks()) {
		if (block.type.name !== "unstyled") return true;
		if (block.textContent.trim().length > 0) return true;
	}

	return false;
}

export function isCollapsed(state: EditorState, node: ProseMirrorNode): boolean {
	const pluginState = CollapsePluginKey.getState(state);
	if (!pluginState) return false;

	return pluginState.collapsed.has(node.attrs.blockId);
}

export function getCollapsedAncestors(state: EditorState, node: ProseMirrorNode): ProseMirrorNode[] {
	const { blockTree } = getBlockBoundaryState(state) ?? {};
	const treeNode = blockTree?.getNodeByBlockKey(node.attrs.blockId);
	const collapsedAncestors = [];

	// Start from parent; if the given node is collapsed it's still visible.
	let current = treeNode?.parent;
	while (current) {
		if (current.block && isCollapsed(state, current.block)) {
			collapsedAncestors.push(current.block);
		}
		current = current.parent;
	}

	collapsedAncestors.reverse();
	return collapsedAncestors;
}

export function isInCollapsedSection(state: EditorState, node: ProseMirrorNode): boolean {
	return getCollapsedAncestors(state, node).length > 0;
}

export function posIsInCollapsedSection(state: EditorState, pos: number): boolean {
	const $pos = state.doc.resolve(pos);

	// Selection is in between blocks within a collapsed section
	if ($pos.depth === 0) return !!$pos.nodeAfter && isInCollapsedSection(state, $pos.nodeAfter);

	const block = $pos.node(1);
	// Selection is in a descendant of a collapsed heading
	if (isInCollapsedSection(state, block)) return true;

	// Selection is in the subheading of a collapsed heading
	if (block.type.name !== "header-one" || !isCollapsed(state, block)) return false;
	return posInSubheading($pos);
}

export function collapseChanged(tr: Transaction, node: ProseMirrorNode): boolean {
	const meta = tr.getMeta(CollapsePluginKey) as CollapsePluginMeta;
	if (!meta) return false;
	return typeof meta[node.attrs.blockId] === "boolean";
}

export function setCollapsedManyTr(
	tr: Transaction,
	nodes: readonly ProseMirrorNode[],
	collapsed: boolean,
): Transaction {
	const meta: MutableCollapsePluginMeta = {};
	for (const node of nodes) {
		invariant(isBlock(node) && node.attrs.blockId, "Nodes must be blocks to be collapsed");
		meta[node.attrs.blockId] = collapsed;
	}
	return (
		tr
			.setMeta(CollapsePluginKey, meta)
			// Tell multicursor plugin to update all the remote cursors
			.setMeta("updateSessionCursor", { sessionId: null })
	);
}

export function setCollapsedMany(view: EditorView, nodes: readonly ProseMirrorNode[], collapsed: boolean): void {
	view.dispatch(setCollapsedManyTr(view.state.tr, nodes, collapsed));
}

export function setCollapsedTr(tr: Transaction, node: ProseMirrorNode, collapsed: boolean): Transaction {
	return setCollapsedManyTr(tr, [node], collapsed);
}

export function setCollapsed(view: EditorView, node: ProseMirrorNode, collapsed: boolean): void {
	setCollapsedMany(view, [node], collapsed);
}

export function getPosAfterSection(state: EditorState, node: ProseMirrorNode): number {
	invariant(node.type.name === "header-one");

	const { blockTree } = getBlockBoundaryState(state) ?? {};
	invariant(blockTree);

	const blockNode = blockTree.getNodeByBlockKey(node.attrs.blockId);
	invariant(blockNode?.block);

	let pos = blockNode.start + blockNode.block.nodeSize;
	for (const childBlock of blockNode.containedBlocks()) {
		pos += childBlock.nodeSize;
	}
	return pos;
}

// The collapsed parameter is needed (instead of reading the collapse plugin
// state from the given editor state) because this function is called to
// initialize the plugin state. In that case, there is no collapse plugin state
// in the editor state yet.
function decorateHiddenNodes(state: EditorState, collapsedBlockIds: Readonly<Set<string>>) {
	const blockBoundary = getBlockBoundaryState(state);
	if (!blockBoundary) return undefined;
	const { blockTree } = blockBoundary;

	const decos = [];

	for (const node of blockTree.root.descendants()) {
		// Only look at the first 2 levels; only headings can collapse, and
		// we only have up to h2.
		if (node.depth > HEADING_MAX_LEVEL) continue;
		if (!node?.block) continue;
		if (!isCollapsible(state, node.block)) continue;
		if (!node.children.length) continue;
		const block = node.block;

		if (collapsedBlockIds.has(block.attrs.blockId)) {
			decos.push(Decoration.node(node.start, node.start + node.block.nodeSize, { "data-collapsed": "" }));
			for (const child of node.descendants()) {
				if (!child.block) continue;
				decos.push(Decoration.node(child.start, child.start + child.block.nodeSize, { class: styles.hidden }));
			}
		}
	}

	return DecorationSet.create(state.doc, decos);
}

type MutableCollapsePluginMeta = { [blockId: string]: boolean };
type CollapsePluginMeta = Readonly<MutableCollapsePluginMeta>;

interface CollapsePluginState {
	collapsed: Set<string>;
	decorations: DecorationSet | undefined;
}

export const CollapsePluginKey = new PluginKey<CollapsePluginState>("collapse");
function makeCollapsePlugin({ initiallyCollapsedBlocks }: { initiallyCollapsedBlocks: readonly string[] }): Plugin {
	return new Plugin<CollapsePluginState>({
		key: CollapsePluginKey,

		state: {
			init: (_config, state) => {
				const collapsed = new Set<string>(initiallyCollapsedBlocks);
				return {
					collapsed,
					decorations: decorateHiddenNodes(state, collapsed),
				};
			},
			apply: (tr, value, _oldState, newState) => {
				const collapseChanges: CollapsePluginMeta | null | undefined = tr.getMeta(CollapsePluginKey);
				if (collapseChanges) {
					for (const [blockId, collapsed] of Object.entries(collapseChanges)) {
						if (collapsed) {
							value.collapsed.add(blockId);
						} else {
							value.collapsed.delete(blockId);
						}

						const { noteId, isPublicTemplateView } = getContext(newState) ?? {};
						if (noteId && !isPublicTemplateView) {
							commitMutation<ToggleCollapsedMutation>(environment, {
								mutation: graphql`
									mutation collapse_toggleCollapsedMutation($noteId: ID!, $blockKey: String!, $collapsed: Boolean!) {
										NoteBlock_SetCollapsed(blockId: { noteId: $noteId, blockKey: $blockKey }, collapsed: $collapsed) {
											block {
												note {
													id
													... on Note {
														collapsedBlockKeys
													}
												}
											}
										}
									}
								`,
								variables: {
									noteId,
									blockKey: blockId,
									collapsed,
								},
							});
						}
					}
				}

				if (tr.docChanged || collapseChanges) {
					value.decorations = decorateHiddenNodes(newState, value.collapsed);
				}

				return value;
			},
		},

		props: {
			decorations: state => CollapsePluginKey.getState(state)?.decorations,
		},
	});
}

const expandEmptySectionsPlugin = new Plugin<void>({
	key: new PluginKey<void>("expand-empty-sections"),

	appendTransaction(trs, _oldState, newState) {
		const { collapsed } = CollapsePluginKey.getState(newState) ?? {};
		const { blockTree } = getBlockBoundaryState(newState) ?? {};
		if (!collapsed || !blockTree) return undefined;
		if (!trs.some(tr => tr.docChanged)) return undefined;
		const meta: MutableCollapsePluginMeta = {};

		let expandedAny = false;
		for (const collapsedBlockId of collapsed) {
			const node = blockTree.getNodeByBlockKey(collapsedBlockId);
			if (!node?.block || !isCollapsible(newState, node.block)) {
				meta[collapsedBlockId] = false;
				expandedAny = true;
			}
		}

		return expandedAny ? newState.tr.setMeta(CollapsePluginKey, meta) : undefined;
	},
});

const expandChangedSectionsPlugin = new Plugin<void>({
	key: new PluginKey<void>("expand-changed-sections"),

	appendTransaction(trs, _oldState, newState) {
		const { collapsed } = CollapsePluginKey.getState(newState) ?? {};
		const { changedInternalTreeNodes } = getBlockBoundaryState(newState) ?? {};
		if (!collapsed || !changedInternalTreeNodes) return undefined;
		const meta: MutableCollapsePluginMeta = {};

		let expandedAny = false;
		if (trs.some(tr => tr.docChanged && !tr.getMeta("remoteChange")) && changedInternalTreeNodes?.length) {
			for (const changedNode of changedInternalTreeNodes) {
				let current: BlockTreeNode | undefined = changedNode;
				while (current) {
					if (current.block && (collapsed?.has(current.block.attrs.blockId) || meta[current.block.attrs.blockId])) {
						meta[current.block.attrs.blockId] = false;
						expandedAny = true;
					}
					current = current.parent;
				}
			}
		}

		return expandedAny ? newState.tr.setMeta(CollapsePluginKey, meta).scrollIntoView() : undefined;
	},
});

function moveToHeaderEndIfNotVisible(state: EditorState, pos: number): number {
	const { blockTree } = getBlockBoundaryState(state) ?? {};
	if (!blockTree) return pos;
	const { doc } = state;

	const $pos = doc.resolve(pos);
	let containingBlock;
	if ($pos.depth === 0) {
		containingBlock = $pos.nodeAfter;
		if (!containingBlock) return pos;
	} else {
		containingBlock = closestBlock(doc.resolve(pos));
	}
	const treeNode = blockTree.getNodeByBlockKey(containingBlock.attrs.blockId);
	if (!treeNode) return pos;

	let outermostCollapsedParent = null;
	for (let current = treeNode.parent; current; current = current.parent) {
		if (current.block && isCollapsed(state, current.block)) {
			outermostCollapsedParent = current;
		}
	}

	if (!outermostCollapsedParent) return pos;
	invariant(outermostCollapsedParent.block != null);
	return outermostCollapsedParent.start + 1 + getHeaderTextSize(outermostCollapsedParent.block);
}

const ensureSelectionEndsVisiblePlugin = new Plugin<void>({
	key: new PluginKey<void>("ensure-selection-ends-visible"),

	// If your cursor ends up within the hidden part of a collapsed section, move
	// it to the end of the heading at the top of that section.
	appendTransaction(trs, _oldState, newState) {
		const { collapsed } = CollapsePluginKey.getState(newState) ?? {};
		const { blockTree } = getBlockBoundaryState(newState) ?? {};
		if (!collapsed || !blockTree || currentlyDragging(newState)) return undefined;
		if (!trs.some(tr => tr.selectionSet || tr.getMeta(CollapsePluginKey))) return undefined;

		const { anchor, head } = newState.selection;
		let newSelection;
		if (newState.selection instanceof NodeSelection) {
			if (!isInCollapsedSection(newState, newState.selection.node)) {
				return undefined;
			}
			newSelection = TextSelection.create(newState.doc, moveToHeaderEndIfNotVisible(newState, anchor));
		} else {
			if (!posIsInCollapsedSection(newState, anchor) && !posIsInCollapsedSection(newState, head)) {
				return undefined;
			}
			newSelection = TextSelection.create(
				newState.doc,
				moveToHeaderEndIfNotVisible(newState, anchor),
				moveToHeaderEndIfNotVisible(newState, head),
			);
		}
		return newState.tr.setSelection(newSelection).scrollIntoView();
	},
});

function getSurroundingHeader($pos: ResolvedPos): ProseMirrorNode | null {
	if ($pos.depth === 0) {
		return null;
	}
	const block = closestBlock($pos);
	if (block.type.name !== "header-one") return null;
	return !posInSubheading($pos) ? block : null;
}

const expandSelectionOverCollapsedSectionPlugin = new Plugin<void>({
	key: new PluginKey<void>("expand-selection-over-collapsed-section"),

	appendTransaction(_trs, _oldState, newState) {
		const { blockTree } = getBlockBoundaryState(newState) ?? {};
		if (!blockTree) return undefined;

		if (newState.selection instanceof NodeSelection) {
			// A NodeSelection over a block can be created by holding cmd/ctrl when
			// clicking on it. If you do that on a collapsed heading, we'll expand
			// the selection to cover the descendants too.
			const { node } = newState.selection;
			if (node.type.name === "header-one" && isCollapsed(newState, node)) {
				const treeNode = blockTree.getNodeByBlockKey(node.attrs.blockId);
				invariant(treeNode?.block);
				const { start } = treeNode;
				const end = getPosAfterSection(newState, node);
				return newState.tr.setSelection(BlockSelection.create(newState.doc, start, end));
			}
		} else {
			// if "to" is within the heading text and "from" is in a different block,
			// expand the selection to contain the whole collapsed section. If "from"
			// is in the heading text and "to" is in another block, we don't need to
			// do anything since the selection will already contain all the blocks
			// between (including the hidden ones).
			const { $from, $to } = newState.selection;

			const headingBlock = getSurroundingHeader($to);
			if (!$from.sameParent($to) && headingBlock) {
				const headingCollapsed = isCollapsed(newState, headingBlock);
				if (headingCollapsed) {
					const newTo = getPosAfterSection(newState, headingBlock);
					return newState.tr.setSelection(BlockSelection.create(newState.doc, $from.before(1), newTo));
				}
			}
		}

		return undefined;
	},
});

// Need an extension even though all we have is a plugin because we need the
// plugin to be initialized after blockBoundary. If we specify it as a plugin
// to the EditorConfig, it will be initialized first.
export class CollapseExtension extends Extension {
	name = "collapse";

	constructor(private initiallyCollapsedBlocks: readonly string[]) {
		super();
	}

	get plugins() {
		return [
			makeCollapsePlugin({ initiallyCollapsedBlocks: this.initiallyCollapsedBlocks }),
			expandEmptySectionsPlugin,
			expandChangedSectionsPlugin,
			// Update selection _after_ potentially expanding sections.
			ensureSelectionEndsVisiblePlugin,
			expandSelectionOverCollapsedSectionPlugin,
		];
	}
}

const RIGHT_CHEVRON_HTML = ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={faChevronRight} />);
const DOWN_CHEVRON_HTML = ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={faChevronDown} />);

interface CollapseExpandButtonData {
	onClick: (e: MouseEvent) => void;
	className?: string;
	collapsed: boolean;
}

export class CollapseExpandButton implements DOMComponent<CollapseExpandButtonData> {
	el: HTMLButtonElement;
	private prevCollapsed?: boolean;

	constructor(data: CollapseExpandButtonData) {
		this.el = document.createElement("button");
		this.el.type = "button";
		this.el.contentEditable = "false";
		this.el.onmousedown = e => e.preventDefault();
		this.update(data);
	}

	update(data: CollapseExpandButtonData) {
		const classNames = [styles.collapseExpandButton, ...(data.className ?? "").split(" ")];
		for (const className of sets.symmetricDifference(classNames, this.el.classList.values())) {
			if (className) this.el.classList.toggle(className);
		}

		this.el.onclick = data.onClick;

		if (data.collapsed !== this.prevCollapsed) {
			this.el.innerHTML = data.collapsed ? RIGHT_CHEVRON_HTML : DOWN_CHEVRON_HTML;
		}
		this.prevCollapsed = data.collapsed;
	}
}
